import { useEffect } from 'react';

/** The body custom property reception.css sizes a typed-in search field by. */
const VAR = '--rc-srch-w';
const FIELD = '.maie .topbar .srch input';

/**
 * THE TOP BAR'S SEARCH FIELD KEEPS ITS WIDTH WHILE YOU TYPE.
 *
 * Empty, the field measures like its prompt (`field-sizing: content` in
 * reception.css), which is the width the board draws. Typing would re-size it
 * to the typed text, and the header — sized from its contents — re-wraps: the
 * search and the bell jumped above the title and the primary dropped a row.
 *
 * So the moment the field takes focus (still empty, still prompt-sized) its
 * width is recorded on <body>, and reception.css holds a field that is not
 * showing its prompt at exactly that width. Mounted once per reception route
 * (index.tsx); the property goes with the route.
 */
export function SearchWidthLock() {
  useEffect(() => {
    const record = (input: HTMLInputElement) => {
      let w = 0;
      if (input.value === '') {
        w = input.getBoundingClientRect().width;
      } else if (!document.body.style.getPropertyValue(VAR)) {
        /* A value that arrived before any focus (rare): measure the prompt. */
        const ctx = document.createElement('canvas').getContext('2d');
        if (ctx) {
          ctx.font = getComputedStyle(input).font;
          w = Math.ceil(ctx.measureText(input.placeholder).width);
        }
      }
      if (w > 0) document.body.style.setProperty(VAR, `${w}px`);
    };
    const onFocus = (e: FocusEvent) => {
      const t = e.target;
      if (t instanceof HTMLInputElement && t.matches(FIELD)) record(t);
    };
    document.addEventListener('focusin', onFocus, true);
    /* A field that already holds focus when the route mounts. */
    const active = document.activeElement;
    if (active instanceof HTMLInputElement && active.matches(FIELD)) record(active);
    return () => {
      document.removeEventListener('focusin', onFocus, true);
      document.body.style.removeProperty(VAR);
    };
  }, []);
  return null;
}
